import { Link } from 'wouter';
import { Button } from '@/shared/components/ui/button';
import { CheckCircle, Wrench } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/shared/components/ui/navigation-menu';

export default function SimplifiedNav() {
  const services = [
    { 
      title: 'Residential Services', 
      href: '/residential-services',
      description: 'Protecting homes and families',
      icon: '🏠',
      features: ['Same-day service', 'Pet & child safe'],
      price: 'From £89'
    },
    { 
      title: 'Commercial Solutions', 
      href: '/commercial-solutions',
      description: 'Business pest management programs',
      icon: '🏢',
      features: ['Food safety certified', 'Scheduled visits'],
      price: 'Custom quotes'
    },
    { 
      title: 'Industrial Solutions', 
      href: '/industrial-solutions',
      description: 'Large-scale facility pest management',
      icon: '🏭',
      features: ['Advanced technology', 'Smart monitoring'],
      price: 'From £449/month'
    },
    { 
      title: 'Emergency Response', 
      href: '/emergency-pest-control',
      description: '24/7 urgent pest control',
      icon: '🚨',
      features: ['Same-day response', 'Out of hours'],
      price: 'From £149'
    },
    { 
      title: 'Hygiene Services', 
      href: '/hygiene-services',
      description: 'Professional hygiene and sanitization solutions',
      icon: '✨',
      features: ['Deep cleaning', 'Air quality management'],
      price: 'From £199'
    },
    { 
      title: 'Specialist Services', 
      href: '/specialist-services',
      description: 'Expert solutions for unique pest challenges',
      icon: '🎯',
      features: ['Bird control', 'Wasp removal'],
      price: 'From £129'
    },
    { 
      title: 'Eco-Friendly Solutions', 
      href: '/eco-recommendation-engine',
      description: 'Sustainable & environmentally safe treatments',
      icon: '🌿',
      features: ['Chemical-free options', 'Organic treatment'],
      price: 'From £99'
    }
  ];

  return (
    <nav className="hidden md:flex items-center space-x-8">
      <Link href="/" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-home">
        Home
      </Link>
      
      {/* Services Dropdown */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className="text-gray-700 hover:text-emerald-600 font-medium">
              Services
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="p-6 w-[880px]">
                {/* Enhanced Header */}
                <div className="mb-6 pb-4 border-b border-emerald-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Professional Services</h3>
                  <p className="text-sm text-gray-600">Complete pest management solutions for every need</p>
                </div>
                
                {/* Services Grid - Enhanced Layout */}
                <div className="grid lg:grid-cols-2 gap-5">
                  {services.map((service) => (
                    <NavigationMenuLink
                      key={service.title}
                      href={service.href}
                      className="block p-5 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="p-3 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-2xl">{service.icon}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-900 group-hover:text-emerald-700 text-base">
                              {service.title}
                            </h4>
                            <span className="text-sm font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                              {service.price}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                          <div className="space-y-1">
                            {service.features.map((feature) => (
                              <div key={feature} className="flex items-center space-x-2">
                                <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                                <span className="text-xs text-gray-600">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </NavigationMenuLink>
                  ))}
                </div>

                {/* Call to Action Footer */}
                <div className="mt-6 pt-4 border-t border-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Emergency Situation?</h5>
                      <p className="text-xs text-gray-600">24/7 rapid response available for urgent pest problems</p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => window.open('tel:07913024492', '_self')}
                        className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Emergency Call
                      </button>
                      <NavigationMenuLink
                        href="/emergency-pest-control"
                        className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Learn More →
                      </NavigationMenuLink>
                    </div>
                  </div>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
      
      {/* Management Tools Dropdown - Redesigned for Better Organization */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className="text-gray-700 hover:text-emerald-600 font-medium">
              Management Tools
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="p-6 w-[720px]">
                {/* Header */}
                <div className="mb-6 pb-4 border-b border-emerald-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Management Tools</h3>
                  <p className="text-sm text-gray-600">Comprehensive business management solutions</p>
                </div>
                
                {/* Core Business Tools */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-emerald-700 mb-3 uppercase tracking-wide">Core Business</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <NavigationMenuLink
                      href="/customer-management"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">👥</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Customer Management</h5>
                          <p className="text-xs text-gray-600 mt-1">Complete customer database with invoicing & payment tracking</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                    
                    <NavigationMenuLink
                      href="/command-center"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">📊</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Business Dashboard</h5>
                          <p className="text-xs text-gray-600 mt-1">Executive metrics, performance tracking & business intelligence</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                    
                    <NavigationMenuLink
                      href="/tenders"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">🏛️</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Tender Library</h5>
                          <p className="text-xs text-gray-600 mt-1">Commercial & public sector contract management system</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                    
                    <NavigationMenuLink
                      href="/services"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <Wrench className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Service Management</h5>
                          <p className="text-xs text-gray-600 mt-1">Comprehensive service tracking, resource management & analytics</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                  </div>
                </div>

                {/* Operations & Documentation */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-emerald-700 mb-3 uppercase tracking-wide">Operations & Documentation</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <NavigationMenuLink
                      href="/forms-hub"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">📋</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Forms & Reports Hub</h5>
                          <p className="text-xs text-gray-600 mt-1">Assessment forms, treatment reports & digital documentation</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                    
                    <NavigationMenuLink
                      href="/field-technician"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">📱</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Field Technician Mobile</h5>
                          <p className="text-xs text-gray-600 mt-1">Mobile-optimized interface for on-site technician operations</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                  </div>
                </div>

                {/* System & Configuration */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-emerald-700 mb-3 uppercase tracking-wide">System & Configuration</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <NavigationMenuLink
                      href="/backend-admin"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">⚙️</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">System Administration</h5>
                          <p className="text-xs text-gray-600 mt-1">User management, system monitoring & configuration settings</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                    
                    <NavigationMenuLink
                      href="/notification-preferences"
                      className="block p-4 rounded-xl hover:bg-emerald-50 transition-all group border border-gray-100 hover:border-emerald-200 hover:shadow-sm"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                          <span className="text-lg">🔔</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 group-hover:text-emerald-700">Notification Center</h5>
                          <p className="text-xs text-gray-600 mt-1">Customize alerts, email notifications & business communications</p>
                        </div>
                      </div>
                    </NavigationMenuLink>
                  </div>
                </div>
                
                {/* Platform Upgrade Footer */}
                <div className="pt-4 border-t border-emerald-100 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Need Advanced Features?</h5>
                      <p className="text-xs text-gray-600">Access comprehensive management suite with advanced AI tools</p>
                    </div>
                    <NavigationMenuLink
                      href="/?brand=aegis360"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Upgrade to Aegis360 →
                    </NavigationMenuLink>
                  </div>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <Link href="/pricing" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-pricing">
        Pricing
      </Link>
      <Link href="/contact" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors" data-testid="nav-contact">
        Contact
      </Link>
      
      {/* Emergency Call Button */}
      <Button 
        onClick={() => window.open('tel:07913024492', '_self')}
        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold"
        data-testid="button-emergency-call"
      >
        Emergency: Call Now
      </Button>
    </nav>
  );
}